import React from 'react';

interface SmartLinkProps extends React.AnchorHTMLAttributes<HTMLAnchorElement> {
  href: string;
  onClick?: (e: React.MouseEvent<HTMLAnchorElement>) => void;
  children: React.ReactNode;
  className?: string;
  id?: string;
  title?: string;
}

/**
 * SmartLink Component
 * Ensures every single link across Dualis Tools behaves as a true HTML anchor tag:
 * 1. Supports native right-click ("Open link in new tab", "Open link in new window", "Copy link address")
 * 2. Supports Ctrl+Click (Win/Linux), Cmd+Click (Mac), and Middle-Click to open in new tab
 * 3. Supports standard Left-Click SPA navigation without causing a full page refresh
 */
export const SmartLink: React.FC<SmartLinkProps> = ({
  href,
  onClick,
  children,
  className = '',
  id,
  title,
  ...props
}) => {
  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    // If the user performed Ctrl+Click, Cmd+Click, Shift+Click, or Middle-click,
    // let the browser handle it naturally to open in a new tab/window.
    if (e.ctrlKey || e.metaKey || e.shiftKey || e.altKey || e.button !== 0) {
      return;
    }

    if (onClick) {
      e.preventDefault();
      onClick(e);
    }
  };

  return (
    <a
      id={id}
      href={href}
      title={title}
      className={className}
      onClick={handleClick}
      {...props}
    >
      {children}
    </a>
  );
};

export default SmartLink;
